import styled from "@emotion/styled";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import React, { useRef, useEffect } from "react";

import IcoIcon from "~/components/IcoIcon";

import { palette } from "~/theme";

import { ScreenReaderText, HiddenFormLabel } from "~/styles/screenReaderText";
import transition from "~/utilities/transition";

const getInputColors = ({ borderColor, theme }) => {
    return css`
        border: 1px solid ${theme.palette[borderColor]};
        color: ${theme.palette[borderColor]};
    `;
};

const getButtonColors = ({ borderColor, theme }) => {
    return css`
        border-left: 1px solid ${theme.palette[borderColor]};
    `;
};

const InputWrapper = styled.div`
    position: relative;
`;

const StyledInput = styled.input`
    ${getInputColors};
    width: 100%;
    margin-bottom: 15px;
    padding: 10px;
    background-color: inherit;
    font-size: 1.6rem;
    font-weight: 500;

    ::placeholder {
        color: ${({ theme }) => theme.palette.white};
        font-size: 1.5rem;
        font-weight: 500;
        font-style: italic;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        padding: 20px;
    }
`;

const StyledIconButton = styled.button`
    ${transition({}, "color")};
    position: absolute;
    top: 0;
    right: 0;
    color: ${({ theme }) => theme.palette.lightBlue};
    font-weight: 900;
    padding: 8px;
    border-left: none;

    @media (min-width: ${({ theme }) => theme.breakpoints.mobileLandscape}) {
        ${getButtonColors};
        padding: 15px;
    }

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.palette.darkBlue};
    }
`;

const Input = ({
    name,
    type,
    buttonScreenReaderText,
    value,
    onChange,
    placeholder = "",
    focus = false,
    borderColor = "darkGray",
    id = "",
    label = "",
}) => {
    const inputRef = useRef();

    useEffect(() => {
        if (focus) {
            inputRef.current.focus();
        }
    }, [focus]);

    return (
        <InputWrapper>
            {label ? (
                <HiddenFormLabel htmlFor={id}>{label}</HiddenFormLabel>
            ) : null}
            <StyledInput
                {...{
                    placeholder,
                    name,
                    type,
                    borderColor,
                    value,
                    onChange,
                    id,
                }}
                ref={inputRef}
            />
            <StyledIconButton
                {...{ borderColor }}
                screenReaderText={buttonScreenReaderText}
                type="Submit"
            >
                <IcoIcon icon="search" />
                <ScreenReaderText>Submit</ScreenReaderText>
            </StyledIconButton>
        </InputWrapper>
    );
};

Input.propTypes = {
    placeholder: PropTypes.string,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    buttonScreenReaderText: PropTypes.string.isRequired,
    focus: PropTypes.bool,
    borderColor: PropTypes.oneOf(Object.keys(palette)),
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    id: PropTypes.string,
};

export default Input;
